import React, { Fragment, FunctionComponent, h } from "preact"; // eslint-disable-line @typescript-eslint/no-unused-vars

import { fundEventTypes, pageTypes } from "../../../../helpers/constants";
import MetaItem from "../MetaItem";
import { IResult } from "../Result";
import CompleteOnline from "../CompleteOnline";

export interface IMetaProps {
  item: IResult;
  publishDateLabel: string;
}

const Meta: FunctionComponent<IMetaProps> = ({ item, publishDateLabel }) => {
  const {
    pageType,
    authors,
    publishDate,
    lastReviewDate,
    readTime,
    reference,
    hasExternalUrl,
    hasCompleteOnlineUrl,
    completeOnlineUrls,
  } = item;

  const documentType = pageType === pageTypes.DOCUMENT;
  const webpageType = pageType === pageTypes.WEBPAGE || pageType === pageTypes.DEFAULT;
  const articleType = pageType === pageTypes.ARTICLE;
  const fundEventType = pageType === pageTypes.FUNDEVENT;
  const hasCompleteOnline = hasCompleteOnlineUrl && completeOnlineUrls;

  if (!documentType && !webpageType && !articleType && !fundEventType) {
    return null;
  }

  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  const formatDate = (date) => {
    const dt = new Date(date);

    return dt ? `${dt.getDate()} ${monthNames[dt.getMonth()]} ${dt.getFullYear()}` : null;
  };

  const formatReviewDate = (date) => {
    const dt = new Date(date);

    return dt ? `${monthNames[dt.getMonth()]} ${dt.getFullYear()}` : null;
  };

  const published = formatDate(publishDate);
  const reviewDate = formatReviewDate(lastReviewDate);

  const renderDocumentMeta = () => {
    return (
      <Fragment>
        {publishDate ? (
          <MetaItem
            name={"Published: "}
            // In cases of external URLs, the date looks similar to: "Published: Refer to document for the date", but the property name is slightly confusing. This is here intentionally.
            value={hasExternalUrl ? publishDateLabel : published}
          />
        ) : (
          ""
        )}
        {lastReviewDate ? (
          <MetaItem
            name={"Last reviewed: "}
            value={reviewDate}
          />
        ) : (
          ""
        )}
        {reference ? (
          <MetaItem
            name={"Ref: "}
            value={`${reference}`}
          />
        ) : (
          ""
        )}
        {hasCompleteOnline ? <CompleteOnline item={item} /> : ""}
      </Fragment>
    );
  };

  const renderWebpageMeta = () => {
    return (
      <Fragment>
        {publishDate ? (
          <MetaItem
            name={"Published: "}
            value={published}
          />
        ) : (
          ""
        )}
        {lastReviewDate ? (
          <MetaItem
            name={"Last reviewed: "}
            value={reviewDate}
          />
        ) : (
          ""
        )}
      </Fragment>
    );
  };

  const renderFundEventMeta = () => {
    return (
      <Fragment>
        <MetaItem
          name={"Event Type: "}
          value={item.fundEventType}
          block={true}
        />
        <MetaItem
          name={"Companies Impacted: "}
          value={item.companiesImpacted}
          block={true}
        />
        <MetaItem
          name={"Effective Date: "}
          value={formatDate(item.effectiveDate)}
          block={true}
        />

        {item.fundEventType == fundEventTypes.CHARGESANDCOMMISSION ||
        item.fundEventType == fundEventTypes.OBJECTIVEANDPOLICYCHANGES ||
        item.fundEventType == fundEventTypes.OTHEREVENTS ? (
          item.oldLabel || item.oldDescription ? (
            <MetaItem
              name={item.oldLabel}
              value={item.oldDescription}
              block={true}
            />
          ) : (
            ""
          )
        ) : (
          ""
        )}

        {item.fundEventType == fundEventTypes.CHARGESANDCOMMISSION ||
        item.fundEventType == fundEventTypes.OBJECTIVEANDPOLICYCHANGES ||
        item.fundEventType == fundEventTypes.OTHEREVENTS ? (
          item.newLabel || item.newDescription ? (
            <MetaItem
              name={item.newLabel}
              value={item.newDescription}
              block={true}
            />
          ) : (
            ""
          )
        ) : (
          ""
        )}

        {item.fundEventType == fundEventTypes.CLOSURES ||
        item.fundEventType == fundEventTypes.MERGERS ||
        item.fundEventType == fundEventTypes.NAMECHANGES ? (
          item.defaultAlternativeFundLabel || item.defaultAlternativeFundDescription ? (
            <MetaItem
              name={item.defaultAlternativeFundLabel}
              value={item.defaultAlternativeFundDescription}
              block={true}
            />
          ) : (
            ""
          )
        ) : (
          ""
        )}

        {item.fundEventType == fundEventTypes.OBJECTIVEANDPOLICYCHANGES ||
        item.fundEventType == fundEventTypes.OTHEREVENTS ? (
          item.changeDescription ? (
            <MetaItem
              name={"Change Description: "}
              value={item.changeDescription}
              block={true}
            />
          ) : (
            ""
          )
        ) : (
          ""
        )}
      </Fragment>
    );
  };

  const renderNewsMeta = () => {
    return (
      <Fragment>
        {publishDate ? (
          <MetaItem
            name={"Published: "}
            value={published}
          />
        ) : (
          ""
        )}
        {lastReviewDate ? (
          <MetaItem
            name={"Last reviewed: "}
            value={reviewDate}
          />
        ) : (
          ""
        )}
        {authors ? (
          <MetaItem
            name={"Author: "}
            value={authors}
          />
        ) : (
          ""
        )}
        {readTime ? (
          <MetaItem
            name={`${readTime} minute read`}
            value={""}
          />
        ) : (
          ""
        )}
      </Fragment>
    );
  };

  return (
    <div className="search-results-meta">
      {documentType && renderDocumentMeta()}
      {webpageType && renderWebpageMeta()}
      {/* HELP_SUPPORT / FAQ don't have meta tags */}
      {articleType && renderNewsMeta()}
      {/* PERSON doesn't have meta tags */}
      {fundEventType && renderFundEventMeta()}
    </div>
  );
};

export default Meta;
